// Full-content view modal: edit, per-platform optimize, image (AI/upload),
// Complete Post Bundle, Sora 2 video clip, and share actions.
// Owns ALL of its interaction state — it mounts fresh each time a content item
// is opened (the page wraps it in <AnimatePresence>{viewingContent && ...}</AnimatePresence>),
// so closing the modal automatically resets edit/optimize/image/video state.
import {
  useState, axios, motion, Link,
  Button, Badge, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Loader2, Sparkles, Copy, Trash2, Check, X, Edit,
  Twitter, Linkedin, Facebook, MessageCircle, Instagram, Video, Download,
  API, toast,
} from "@/pages/shared";
import PodcastClipReels from "../PodcastClipReels";
import { CLAIMS } from "@/config/claims";

export const ContentViewModal = ({ content, setContent, onClose, refresh, share, user, token }) => {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizedContent, setOptimizedContent] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [videoStatus, setVideoStatus] = useState(""); // queued | processing | completed | failed
  const [videoUrl, setVideoUrl] = useState("");
  const [videoError, setVideoError] = useState("");
  const [videoFormat, setVideoFormat] = useState("vertical"); // vertical | landscape
  const [insertingDisclosure, setInsertingDisclosure] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Disclosure reminder: tracked short-links use the /r/{code} pattern
  const hasTrackedLink = /\/r\//.test(content.generated_content || "");
  const hasDisclosure = /disclosure|affiliate link|#ad\b/i.test(content.generated_content || "");

  const insertDisclosure = async () => {
    setInsertingDisclosure(true);
    try {
      const prof = await axios.get(`${API}/account/profile`, authHeaders);
      const line = (prof.data.disclosure_line || "").trim() || CLAIMS.defaultDisclosure;
      const updated = `${content.generated_content}\n\n${line}`;
      await axios.put(`${API}/content/${content.id}`, { generated_content: updated }, authHeaders);
      setContent({ ...content, generated_content: updated });
      refresh();
      toast.success("Disclosure added");
    } catch (e) { toast.error("Failed to insert disclosure"); }
    setInsertingDisclosure(false);
  };

  const startEditing = () => {
    setEditing(true);
    setEditText(content.generated_content);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/${content.id}`, { generated_content: editText }, authHeaders);
      toast.success("Content updated!");
      setContent({ ...content, generated_content: editText });
      setEditing(false);
      refresh();
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  const optimizeForPlatform = async (platform) => {
    setOptimizing(true);
    setOptimizedContent(null);
    try {
      const response = await axios.post(`${API}/content/optimize`, { content_id: content.id, platform }, authHeaders);
      setOptimizedContent(response.data);
      toast.success(`Optimized for ${platform}!`);
    } catch (e) { toast.error("Failed to optimize"); }
    setOptimizing(false);
  };

  const handleUploadImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)"); return; }
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await axios.post(`${API}/content/${content.id}/upload-image`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        toast.success("Image uploaded!");
        setContent({ ...content, image_url: res.data.image_url, image_source: "upload" });
        refresh();
      } catch (e) { toast.error("Failed to upload image"); }
      setUploadingImage(false);
    };
    input.click();
  };

  const handleGenerateImage = async () => {
    setGeneratingImage(true);
    try {
      const res = await axios.post(`${API}/content/${content.id}/generate-image`, {}, authHeaders);
      toast.success("AI image generated!");
      setContent({ ...content, image_url: res.data.image_url, image_source: "ai_generated" });
      refresh();
    } catch (e) { toast.error("Failed to generate image"); }
    setGeneratingImage(false);
  };

  const handleRemoveImage = async () => {
    try {
      await axios.delete(`${API}/content/${content.id}/image`, authHeaders);
      toast.success("Image removed");
      setContent({ ...content, image_url: null, image_source: null });
      refresh();
    } catch (e) { toast.error("Failed to remove image"); }
  };

  const handleCompletePost = async () => {
    setBundling(true);
    try {
      const res = await axios.post(`${API}/content/${content.id}/complete-post`, {}, authHeaders);
      toast.success("Complete Post bundle ready");
      setContent({
        ...content,
        generated_content: res.data.caption,
        image_url: res.data.image_url || content.image_url,
        bundle: { hashtags: res.data.hashtags, alt_text: res.data.alt_text, first_comment: res.data.first_comment },
      });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to build complete post");
    }
    setBundling(false);
  };

  const copyAllBundle = () => {
    if (!content.bundle) return;
    const parts = [content.generated_content];
    if (content.bundle.hashtags?.length) parts.push("\n" + content.bundle.hashtags.join(" "));
    if (content.bundle.first_comment) parts.push("\n\n💬 First comment:\n" + content.bundle.first_comment);
    if (content.bundle.alt_text) parts.push("\n\n♿ Alt text:\n" + content.bundle.alt_text);
    navigator.clipboard.writeText(parts.join(""));
    toast.success("Whole bundle copied — caption + hashtags + alt text + first comment");
  };

  const handleGenerateVideo = async () => {
    if (user?.plan === "free") {
      toast.error("Video clips require Pro or Enterprise. Please upgrade.");
      return;
    }
    setGeneratingVideo(true);
    setVideoStatus("queued");
    setVideoUrl("");
    setVideoError("");

    // Build a concise Sora 2 prompt from the post topic + a short slice of the body.
    const body = (content.generated_content || "").replace(/\s+/g, " ").slice(0, 300);
    const prompt = `Short-form social video about: ${content.topic}. ${body}`.slice(0, 1000);

    try {
      const res = await axios.post(`${API}/videos/generate`, {
        prompt,
        size: videoFormat,
        duration: 4,
        model: "sora-2",
        content_id: content.id,
      }, authHeaders);

      const jobId = res.data.job_id;
      toast.success("Video queued. Generating...");

      // Poll every 5s, max ~3 minutes (36 attempts) so we don't loop forever.
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const j = await axios.get(`${API}/videos/jobs/${jobId}`, authHeaders);
          const job = j.data;
          setVideoStatus(job.status);
          if (job.status === "completed" && job.video_url) {
            const fullUrl = `${API.replace(/\/api$/, "")}${job.video_url}`;
            setVideoUrl(fullUrl);
            setGeneratingVideo(false);
            toast.success("Video ready!");
            // Reflect the persisted link locally so reopening the modal still shows it
            setContent({ ...content, video_url: job.video_url, video_size: videoFormat });
            refresh();
            return;
          }
          if (job.status === "failed") {
            setVideoError(job.error || "Video generation failed");
            setGeneratingVideo(false);
            toast.error(job.error || "Video generation failed");
            return;
          }
          if (attempts < 36) setTimeout(poll, 5000);
          else {
            setVideoError("Timed out waiting for video. Check /dashboard/videos for status.");
            setGeneratingVideo(false);
          }
        } catch (e) {
          setVideoError(e.response?.data?.detail || "Failed to fetch job status");
          setGeneratingVideo(false);
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      setGeneratingVideo(false);
      setVideoStatus("");
      toast.error(e.response?.data?.detail || "Failed to start video generation");
    }
  };

  const handleRemoveVideo = async () => {
    try {
      await axios.delete(`${API}/content/${content.id}/video`, authHeaders);
      toast.success("Video removed");
      setVideoUrl("");
      setVideoStatus("");
      setVideoError("");
      setContent({ ...content, video_url: null, video_size: null });
      refresh();
    } catch (e) { toast.error("Failed to remove video"); }
  };

  const downloadImage = (imageUrl) => {
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `creatara-content-image.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="secondary">{content.content_type}</Badge>
              <span className="text-xs text-muted-foreground">{new Date(content.created_at).toLocaleDateString()}</span>
            </div>
            <h3 className="font-semibold">{content.topic}</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
        </div>

        <div className="p-4">
          {editing ? (
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full h-64 bg-white/5 border border-white/10 rounded-lg p-3 text-sm leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="edit-content-textarea"
            />
          ) : (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{content.generated_content}</p>
          )}
        </div>

        {!editing && hasTrackedLink && !hasDisclosure && (
          <div className="mx-4 mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-start justify-between gap-3" data-testid="disclosure-reminder">
            <p className="text-xs text-amber-300 leading-relaxed">This post contains a tracked link — remember your affiliate disclosure. You can save a reusable line in <Link to="/dashboard/settings" className="underline">Settings</Link>.</p>
            <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-7 text-xs whitespace-nowrap flex-shrink-0" onClick={insertDisclosure} disabled={insertingDisclosure} data-testid="insert-disclosure-btn">
              {insertingDisclosure ? <Loader2 className="w-3 h-3 animate-spin" /> : "Insert my disclosure"}
            </Button>
          </div>
        )}

        {/* Character Count & Platform Limits */}
        <div className="px-4 py-2 bg-white/5 border-t border-border">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <span className="font-medium">{content.generated_content.length} characters</span>
            <span className={content.generated_content.length <= 280 ? "text-teal-500" : "text-amber-500"}>
              X: {content.generated_content.length <= 280 ? "✓" : `${content.generated_content.length - 280} over`}
            </span>
            <span className={content.generated_content.length <= 3000 ? "text-teal-500" : "text-amber-500"}>
              LinkedIn: {content.generated_content.length <= 3000 ? "✓" : `over`}
            </span>
            <span className={content.generated_content.length <= 500 ? "text-teal-500" : "text-amber-500"}>
              Facebook: {content.generated_content.length <= 500 ? "✓" : `over optimal`}
            </span>
            <span className={content.generated_content.length <= 2200 ? "text-teal-500" : "text-amber-500"}>
              Instagram: {content.generated_content.length <= 2200 ? "✓" : `${content.generated_content.length - 2200} over`}
            </span>
            <span className={content.generated_content.length <= 300 ? "text-teal-500" : "text-amber-500"}>
              TikTok: {content.generated_content.length <= 300 ? "✓" : `over optimal`}
            </span>
          </div>
        </div>

        {/* Optimize for Platform */}
        <div className="px-4 py-3 border-t border-border bg-white/5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">Optimize for:</span>
            <Button size="sm" variant="outline" disabled={optimizing} onClick={() => optimizeForPlatform("twitter")} className="text-blue-400 border-blue-400/30">
              {optimizing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Twitter className="w-3 h-3 mr-1" />}X (280)
            </Button>
            <Button size="sm" variant="outline" disabled={optimizing} onClick={() => optimizeForPlatform("linkedin")} className="text-blue-600 border-blue-600/30">
              {optimizing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Linkedin className="w-3 h-3 mr-1" />}LinkedIn
            </Button>
            <Button size="sm" variant="outline" disabled={optimizing} onClick={() => optimizeForPlatform("facebook")} className="text-blue-500 border-blue-500/30">
              {optimizing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Facebook className="w-3 h-3 mr-1" />}Facebook
            </Button>
            <Button size="sm" variant="outline" disabled={optimizing} onClick={() => optimizeForPlatform("whatsapp")} className="text-green-500 border-green-500/30">
              {optimizing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <MessageCircle className="w-3 h-3 mr-1" />}WhatsApp
            </Button>
            <Button size="sm" variant="outline" disabled={optimizing} onClick={() => optimizeForPlatform("instagram")} className="text-pink-500 border-pink-500/30">
              {optimizing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Instagram className="w-3 h-3 mr-1" />}Instagram
            </Button>
            <Button size="sm" variant="outline" disabled={optimizing} onClick={() => optimizeForPlatform("tiktok")} className="text-cyan-400 border-cyan-400/30">
              {optimizing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Video className="w-3 h-3 mr-1" />}TikTok
            </Button>
          </div>

          {optimizedContent && (
            <div className="mt-3 p-3 rounded-lg bg-teal-500/10 border border-teal-500/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-teal-500 font-medium">
                  Optimized for {optimizedContent.platform} ({optimizedContent.character_count} chars)
                </span>
                <Button size="sm" variant="ghost" className="h-7 text-teal-500" onClick={() => {
                  navigator.clipboard.writeText(optimizedContent.optimized_content);
                  toast.success("Optimized content copied!");
                }}><Copy className="w-3 h-3 mr-1" />Copy</Button>
              </div>
              <p className="text-sm whitespace-pre-wrap">{optimizedContent.optimized_content}</p>
            </div>
          )}
        </div>

        {/* Image Section */}
        <div className="px-4 py-3 border-t border-border bg-white/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Image</span>
            {!content.image_url && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={uploadingImage} onClick={handleUploadImage} className="text-amber-500 border-amber-500/30" data-testid="upload-image-btn">
                  {uploadingImage ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1 rotate-180" />}Upload Image
                </Button>
                <Button size="sm" variant="outline" disabled={generatingImage} onClick={handleGenerateImage} className="text-purple-500 border-purple-500/30" data-testid="generate-image-btn">
                  {generatingImage ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Generating...</> : <><Sparkles className="w-3 h-3 mr-1" />Generate AI Image</>}
                </Button>
              </div>
            )}
          </div>
          {generatingImage && (
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-purple-500 mx-auto mb-2" />
              <p className="text-sm text-purple-400">Generating AI image... This may take up to 60 seconds.</p>
            </div>
          )}
          {content.image_url && (
            <div className="relative rounded-lg overflow-hidden bg-black/20">
              <img src={content.image_url} alt="Content image" className="w-full max-h-64 object-contain" data-testid="content-image" />
              <div className="absolute top-2 right-2 flex gap-1">
                <Button size="sm" className="h-7 bg-black/60 hover:bg-black/80 text-white" onClick={() => downloadImage(content.image_url)} data-testid="download-image-btn">
                  <Download className="w-3 h-3 mr-1" />Download
                </Button>
                <Button size="sm" className="h-7 bg-violet-600/80 hover:bg-violet-600 text-white" onClick={handleRemoveImage} data-testid="remove-image-btn">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              <div className="absolute bottom-2 left-2">
                <Badge className={content.image_source === "ai_generated" ? "bg-purple-500/80" : "bg-amber-500/80"}>
                  {content.image_source === "ai_generated" ? "AI Generated" : "Uploaded"}
                </Badge>
              </div>
            </div>
          )}
        </div>

        {/* Complete Post Bundle Section — image + caption + hashtags + alt + first comment all-in-one */}
        {["social", "instagram", "facebook", "linkedin", "twitter"].includes(content.content_type) && (
          <div className="px-4 py-3 border-t border-border bg-gradient-to-br from-violet-500/5 to-purple-500/5" data-testid="complete-post-section">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <span className="text-sm font-semibold flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-violet-500" /> Complete Post Bundle</span>
                <p className="text-xs text-muted-foreground">One click → finished post with caption, image, hashtags, alt text, and a pinned first comment.</p>
              </div>
              <Button size="sm" disabled={bundling} onClick={handleCompletePost} className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="complete-post-btn">
                {bundling ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Building (~20s)…</> : <><Sparkles className="w-3 h-3 mr-1" />{content.bundle ? "Rebuild Bundle" : "Build Complete Post"}</>}
              </Button>
            </div>
            {content.bundle && (
              <div className="mt-3 space-y-3" data-testid="bundle-output">
                {content.bundle.hashtags?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs text-cyan-400">Hashtags ({content.bundle.hashtags.length})</Label>
                      <button onClick={() => { navigator.clipboard.writeText(content.bundle.hashtags.join(" ")); toast.success("Hashtags copied"); }} className="text-xs text-muted-foreground hover:text-foreground" data-testid="copy-hashtags-btn">Copy</button>
                    </div>
                    <p className="text-xs text-cyan-300 break-words">{content.bundle.hashtags.join(" ")}</p>
                  </div>
                )}
                {content.bundle.alt_text && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs text-amber-400">Alt text (accessibility)</Label>
                      <button onClick={() => { navigator.clipboard.writeText(content.bundle.alt_text); toast.success("Alt text copied"); }} className="text-xs text-muted-foreground hover:text-foreground" data-testid="copy-alt-btn">Copy</button>
                    </div>
                    <p className="text-xs text-amber-200/80">{content.bundle.alt_text}</p>
                  </div>
                )}
                {content.bundle.first_comment && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs text-teal-400">Pin this as first comment</Label>
                      <button onClick={() => { navigator.clipboard.writeText(content.bundle.first_comment); toast.success("First comment copied"); }} className="text-xs text-muted-foreground hover:text-foreground" data-testid="copy-first-comment-btn">Copy</button>
                    </div>
                    <p className="text-xs text-teal-200/80 whitespace-pre-wrap">{content.bundle.first_comment}</p>
                  </div>
                )}
                <Button size="sm" onClick={copyAllBundle} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="copy-bundle-btn">
                  <Copy className="w-3 h-3 mr-1.5" />Copy entire bundle
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Video Clip Section (Sora 2) */}
        <div className="px-4 py-3 border-t border-border bg-white/5" data-testid="video-clip-section">
          {(() => {
            const persistedVideoPath = content.video_url;
            const persistedFullUrl = persistedVideoPath
              ? `${API.replace(/\/api$/, "")}${persistedVideoPath}`
              : "";
            const displayUrl = videoUrl || persistedFullUrl;
            const hasVideo = Boolean(displayUrl);
            return (
              <>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Video Clip</span>
                    <Badge variant="secondary" className="text-[10px]">Sora 2 · 4s</Badge>
                  </div>
                  {!hasVideo && !generatingVideo && (
                    <div className="flex gap-2 items-center">
                      <Select value={videoFormat} onValueChange={setVideoFormat}>
                        <SelectTrigger className="h-8 w-32 bg-white/5 border-white/10 text-xs" data-testid="video-format-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vertical">TikTok/Reel (9:16)</SelectItem>
                          <SelectItem value="landscape">YouTube (16:9)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleGenerateVideo}
                        disabled={user?.plan === "free"}
                        className="text-cyan-400 border-cyan-400/30"
                        data-testid="generate-video-btn"
                      >
                        <Video className="w-3 h-3 mr-1" />
                        {user?.plan === "free" ? "Upgrade to use" : "Generate Video Clip"}
                      </Button>
                    </div>
                  )}
                </div>
                {user?.plan === "free" && !generatingVideo && !hasVideo && (
                  <p className="text-xs text-muted-foreground">Video generation is a Pro+ feature. Upgrade in <Link to="/dashboard/settings" className="underline">Settings</Link> to turn any post into a TikTok/Reel.</p>
                )}
                {generatingVideo && (
                  <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-center" data-testid="video-loading">
                    <Loader2 className="w-6 h-6 animate-spin text-cyan-400 mx-auto mb-2" />
                    <p className="text-sm text-cyan-300">
                      {videoStatus === "queued" && "Queued — your clip is starting…"}
                      {videoStatus === "processing" && "Rendering your video clip (this can take 1–3 minutes)…"}
                      {!videoStatus && "Submitting…"}
                    </p>
                  </div>
                )}
                {videoError && !generatingVideo && (
                  <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20 text-sm text-violet-300" data-testid="video-error">
                    {videoError}
                  </div>
                )}
                {hasVideo && !generatingVideo && (
                  <div className="relative rounded-lg overflow-hidden bg-black/40" data-testid="content-video">
                    <video src={displayUrl} controls className="w-full max-h-80 mx-auto" />
                    <div className="absolute top-2 right-2 flex gap-1">
                      <a href={displayUrl} download="creatara-video-clip.mp4">
                        <Button size="sm" className="h-7 bg-black/60 hover:bg-black/80 text-white" data-testid="download-video-btn">
                          <Download className="w-3 h-3 mr-1" />Download
                        </Button>
                      </a>
                      <Button size="sm" className="h-7 bg-violet-600/80 hover:bg-violet-600 text-white" onClick={handleRemoveVideo} data-testid="remove-video-btn">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    {content.video_size && (
                      <div className="absolute bottom-2 left-2">
                        <Badge className="bg-cyan-500/80">
                          {content.video_size === "vertical" ? "TikTok/Reel" : "YouTube"}
                        </Badge>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Podcast Template — one-click Sora 2 reels per clip suggestion */}
        <PodcastClipReels content={content} userPlan={user?.plan} token={token} />

        <div className="p-4 border-t border-border flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-teal-500 hover:bg-teal-600" data-testid="save-edit-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-btn"><X className="w-4 h-4 mr-2" />Cancel</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={startEditing} data-testid="edit-content-btn"><Edit className="w-4 h-4 mr-2" />Edit</Button>
              <Button size="sm" onClick={() => share.copyWithSignature(content.generated_content)}><Copy className="w-4 h-4 mr-2" />Copy All</Button>
              <div className="flex items-center gap-1 ml-auto">
                <span className="text-xs text-muted-foreground mr-2">Share:</span>
                <Button size="sm" variant="outline" className="text-blue-400 border-blue-400/30" onClick={() => share.shareToTwitter(content)}><Twitter className="w-4 h-4 mr-1" />X</Button>
                <Button size="sm" variant="outline" className="text-blue-600 border-blue-600/30" onClick={() => share.shareToLinkedIn(content)}><Linkedin className="w-4 h-4 mr-1" />LinkedIn</Button>
                <Button size="sm" variant="outline" className="text-blue-500 border-blue-500/30" onClick={() => share.shareToFacebook(content)}><Facebook className="w-4 h-4 mr-1" />Facebook</Button>
                <Button size="sm" variant="outline" className="text-green-500 border-green-500/30" onClick={() => share.shareToWhatsApp(content)}><MessageCircle className="w-4 h-4 mr-1" />WhatsApp</Button>
                <Button size="sm" variant="outline" className="text-pink-500 border-pink-500/30" onClick={() => share.copyForInstagram(content)}><Instagram className="w-4 h-4 mr-1" />Instagram</Button>
                <Button size="sm" variant="outline" className="text-cyan-400 border-cyan-400/30" onClick={() => share.copyForTikTok(content)}><Video className="w-4 h-4 mr-1" />TikTok</Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};
